import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useSidebar } from '../../contexts/SidebarContext';
import HeaderNavigation from '../../components/ui/HeaderNavigation';
import MainSidebar from '../../components/ui/MainSidebar';
import BreadcrumbNavigation from '../../components/ui/BreadcrumbNavigation';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import lifecycleService from '../../services/lifecycleService';
import HireApplicantModal from './components/HireApplicantModal';
import OnboardingChecklistModal from './components/OnboardingChecklistModal';
import {
  ACTION_TYPE_LABELS,
  ACTION_TYPE_STYLES,
  FUNNEL_STAGE_COLORS,
  LIFECYCLE_PATHS,
  STAGE_LABELS,
  atsWithApplicant,
  atsWithStatus,
  auditEntityPath,
} from './utils/lifecycleNavigation';

const QUICK_LINKS = [
  { label: 'Applicant Tracking', icon: 'Users', path: LIFECYCLE_PATHS.ats, color: 'text-indigo-600 bg-indigo-50' },
  { label: 'Offer Letters', icon: 'FileText', path: LIFECYCLE_PATHS.atsOffers, color: 'text-amber-600 bg-amber-50' },
  { label: 'Deployments', icon: 'MapPin', path: LIFECYCLE_PATHS.deployments, color: 'text-green-600 bg-green-50' },
  { label: 'Employees', icon: 'UserCheck', path: LIFECYCLE_PATHS.employees, color: 'text-purple-600 bg-purple-50' },
];

export default function LifecycleDashboard() {
  const { user, userProfile, loading: authLoading } = useAuth();
  const { isCollapsed } = useSidebar();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState(null);
  const [hireModal, setHireModal] = useState(null);
  const [onboardingEmployee, setOnboardingEmployee] = useState(null);

  const loadDashboard = useCallback(async (silent = false) => {
    if (!user) return;
    if (silent) setRefreshing(true);
    else setLoading(true);

    const result = await lifecycleService.getDashboard();
    if (result.success) {
      setStats(result.data);
      setError(null);
    } else {
      setError(result.error || 'Failed to load lifecycle data');
    }

    if (silent) setRefreshing(false);
    else setLoading(false);
  }, [user]);

  useEffect(() => {
    if (!authLoading && userProfile?.role === 'employee') {
      navigate('/erp/employee-self-service', { replace: true });
    }
  }, [userProfile, authLoading, navigate]);

  useEffect(() => {
    loadDashboard();
  }, [loadDashboard]);

  const handlePendingAction = (action) => {
    switch (action.type) {
      case 'ready_to_hire':
        setHireModal({
          applicantId: action.entity_id,
          offerId: action.meta?.offer_id,
          applicantName: action.meta?.applicant_name,
          requireDeployment: false,
        });
        break;
      case 'deployment_required':
        navigate(`${LIFECYCLE_PATHS.deployments}?applicantId=${encodeURIComponent(action.entity_id)}`);
        break;
      case 'onboarding_incomplete':
        setOnboardingEmployee({
          id: action.entity_id,
          full_name: action.label?.split('—').pop()?.trim(),
          employee_code: action.meta?.employee_code,
        });
        break;
      case 'offer_pending':
        if (action.meta?.applicant_id) {
          navigate(atsWithApplicant(action.meta.applicant_id));
        } else {
          navigate(LIFECYCLE_PATHS.atsOffers);
        }
        break;
      default:
        if (action.entity_type === 'job_application') {
          navigate(atsWithApplicant(action.entity_id));
        }
    }
  };

  const funnel = stats?.funnel || {};
  const funnelTotal = Math.max(
    Object.values(funnel).reduce((sum, n) => sum + (n || 0), 0),
    1
  );

  return (
    <div className="min-h-screen bg-gray-50">
      <HeaderNavigation onMenuClick={() => setSidebarOpen(true)} />
      <MainSidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />
      <div className={`transition-all duration-300 ${isCollapsed ? 'lg:ml-20' : 'lg:ml-64'}`}>
        <div className="p-4 md:p-6 lg:p-8">
          <BreadcrumbNavigation
            items={[
              { label: 'Dashboard', path: '/erp/dashboard-overview' },
              { label: 'Employee Lifecycle', path: '/erp/lifecycle-dashboard' },
            ]}
          />

          <div className="mt-4 mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Employee Lifecycle</h1>
              <p className="text-gray-600 mt-1">
                Unified pipeline from application through hire, onboarding, deployment, and exit.
              </p>
            </div>
            <Button
              variant="outline"
              size="sm"
              iconName="RefreshCw"
              loading={refreshing}
              onClick={() => loadDashboard(true)}
            >
              Refresh
            </Button>
          </div>

          {/* Quick navigation */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
            {QUICK_LINKS.map((link) => (
              <button
                key={link.path}
                type="button"
                onClick={() => navigate(link.path)}
                className={`flex items-center gap-3 p-3 rounded-xl border border-gray-200 bg-white hover:shadow-md transition-shadow text-left`}
              >
                <span className={`p-2 rounded-lg ${link.color}`}>
                  <Icon name={link.icon} size={18} />
                </span>
                <span className="text-sm font-medium text-gray-900">{link.label}</span>
              </button>
            ))}
          </div>

          {loading && (
            <div className="text-gray-500 py-12 text-center">Loading lifecycle data…</div>
          )}

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-4 mb-6">{error}</div>
          )}

          {!loading && stats && (
            <>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                <StatCard
                  label="Pending Offers"
                  value={stats.pending_offers}
                  color="amber"
                  onClick={() => navigate(LIFECYCLE_PATHS.atsOffers)}
                  hint="View offer letters"
                />
                <StatCard
                  label="Onboarding In Progress"
                  value={stats.pending_onboarding}
                  color="blue"
                  onClick={() => navigate(`${LIFECYCLE_PATHS.employees}?status=onboarding`)}
                  hint="View onboarding employees"
                />
                <StatCard
                  label="Active Deployments"
                  value={stats.active_deployments}
                  color="green"
                  onClick={() => navigate(LIFECYCLE_PATHS.deployments)}
                  hint="Manage deployments"
                />
                <StatCard
                  label="Employees (Active)"
                  value={stats.employees?.active ?? 0}
                  color="indigo"
                  onClick={() => navigate(`${LIFECYCLE_PATHS.employees}?status=active`)}
                  hint="Employee directory"
                />
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
                {/* Funnel */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="text-lg font-semibold text-gray-900">Applicant Funnel</h2>
                    <span className="text-xs text-gray-500">Click a stage to filter ATS</span>
                  </div>
                  <div className="space-y-3">
                    {Object.entries(funnel).map(([stage, count]) => (
                      <button
                        key={stage}
                        type="button"
                        onClick={() => navigate(atsWithStatus(stage))}
                        className="w-full text-left group rounded-lg p-2 -mx-2 hover:bg-gray-50 transition-colors"
                      >
                        <div className="flex justify-between text-sm mb-1">
                          <span className="text-gray-700 group-hover:text-indigo-700 font-medium">
                            {STAGE_LABELS[stage] || stage}
                          </span>
                          <span className="font-semibold text-gray-900">{count}</span>
                        </div>
                        <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                          <div
                            className={`h-full rounded-full transition-all ${FUNNEL_STAGE_COLORS[stage] || 'bg-indigo-500'}`}
                            style={{ width: `${Math.round(((count || 0) / funnelTotal) * 100)}%` }}
                          />
                        </div>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Pending actions */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="text-lg font-semibold text-gray-900">Pending Actions</h2>
                    <span className="text-xs text-gray-500">
                      {stats.pending_actions?.length || 0} items
                    </span>
                  </div>
                  {stats.pending_actions?.length ? (
                    <ul className="divide-y divide-gray-100 max-h-80 overflow-y-auto">
                      {stats.pending_actions.map((action, idx) => {
                        const styles = ACTION_TYPE_STYLES[action.type] || ACTION_TYPE_STYLES.offer_pending;
                        const actionLabel = ACTION_TYPE_LABELS[action.type] || 'Open';
                        return (
                          <li key={`${action.type}-${action.entity_id}-${idx}`} className="py-3">
                            <div className="flex items-start gap-3">
                              <span className={`mt-2 w-2 h-2 rounded-full flex-shrink-0 ${styles.dot}`} />
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-gray-900">{action.label}</p>
                                <span className={`inline-block mt-1 text-xs px-2 py-0.5 rounded-full ${styles.badge}`}>
                                  {actionLabel}
                                </span>
                              </div>
                              <Button
                                size="xs"
                                variant="outline"
                                onClick={() => handlePendingAction(action)}
                              >
                                {actionLabel}
                              </Button>
                            </div>
                          </li>
                        );
                      })}
                    </ul>
                  ) : (
                    <p className="text-gray-500 text-sm">No pending lifecycle actions.</p>
                  )}
                </div>
              </div>

              {/* Onboarding progress */}
              {stats.onboarding_employees?.length > 0 && (
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="text-lg font-semibold text-gray-900">Onboarding Progress</h2>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => navigate(`${LIFECYCLE_PATHS.employees}?status=onboarding`)}
                    >
                      View all
                    </Button>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                    {stats.onboarding_employees.map((emp) => (
                      <button
                        key={emp.id}
                        type="button"
                        onClick={() => setOnboardingEmployee(emp)}
                        className="text-left p-4 rounded-lg border border-gray-200 hover:border-indigo-300 hover:shadow-sm transition-all"
                      >
                        <div className="flex justify-between items-start mb-2">
                          <div>
                            <p className="font-medium text-gray-900">{emp.full_name}</p>
                            <p className="text-xs text-gray-500">{emp.employee_code}</p>
                          </div>
                          <span className="text-sm font-semibold text-indigo-600">{emp.progress_pct}%</span>
                        </div>
                        <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                          <div
                            className="h-full bg-indigo-500 rounded-full"
                            style={{ width: `${emp.progress_pct}%` }}
                          />
                        </div>
                        <p className="text-xs text-gray-500 mt-2">
                          {emp.completed_items}/{emp.total_items} tasks complete · {emp.status}
                        </p>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Audit trail */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">Recent Lifecycle Events</h2>
                {stats.recent_audit?.length ? (
                  <div className="overflow-x-auto">
                    <table className="min-w-full text-sm">
                      <thead>
                        <tr className="text-left text-gray-500 border-b">
                          <th className="pb-2 pr-4">Time</th>
                          <th className="pb-2 pr-4">Action</th>
                          <th className="pb-2 pr-4">Details</th>
                          <th className="pb-2">By</th>
                        </tr>
                      </thead>
                      <tbody>
                        {stats.recent_audit.map((row) => {
                          const link = auditEntityPath(row);
                          return (
                            <tr
                              key={row.id}
                              className={`border-b border-gray-50 ${link ? 'cursor-pointer hover:bg-gray-50' : ''}`}
                              onClick={() => link && navigate(link)}
                            >
                              <td className="py-2.5 pr-4 text-gray-600 whitespace-nowrap">
                                {new Date(row.created_at).toLocaleString()}
                              </td>
                              <td className="py-2.5 pr-4">
                                <span className="font-medium text-gray-900">{row.action}</span>
                              </td>
                              <td className="py-2.5 pr-4 text-gray-600 max-w-xs truncate">
                                {row.description || `${row.entity_type} · ${row.entity_id?.slice(0, 8)}…`}
                              </td>
                              <td className="py-2.5 text-gray-600">{row.user_name || 'System'}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <p className="text-gray-500 text-sm">No audit events yet. Actions like hires, status changes, and onboarding updates will appear here.</p>
                )}
              </div>
            </>
          )}
        </div>
      </div>

      <HireApplicantModal
        isOpen={!!hireModal}
        applicantId={hireModal?.applicantId}
        offerId={hireModal?.offerId}
        applicantName={hireModal?.applicantName}
        requireDeployment={hireModal?.requireDeployment}
        onClose={() => setHireModal(null)}
        onSuccess={() => loadDashboard(true)}
      />

      <OnboardingChecklistModal
        isOpen={!!onboardingEmployee}
        employee={onboardingEmployee}
        onClose={() => setOnboardingEmployee(null)}
        onUpdated={() => loadDashboard(true)}
      />
    </div>
  );
}

function StatCard({ label, value, color, onClick, hint }) {
  const colors = {
    amber: 'bg-amber-50 text-amber-800 border-amber-200 hover:border-amber-300',
    blue: 'bg-blue-50 text-blue-800 border-blue-200 hover:border-blue-300',
    green: 'bg-green-50 text-green-800 border-green-200 hover:border-green-300',
    indigo: 'bg-indigo-50 text-indigo-800 border-indigo-200 hover:border-indigo-300',
  };
  return (
    <button
      type="button"
      onClick={onClick}
      className={`rounded-xl border p-4 text-left w-full transition-all hover:shadow-md ${colors[color] || colors.indigo}`}
    >
      <p className="text-xs font-medium opacity-80">{label}</p>
      <p className="text-2xl font-bold mt-1">{value ?? 0}</p>
      {hint && <p className="text-xs opacity-70 mt-1">{hint} →</p>}
    </button>
  );
}
